import { Vector3 } from "three";
import { latLngToVec3 } from "./geo";

export interface SolarState {
  /** Subsolar latitude in degrees (= solar declination). */
  lat: number;
  /** Subsolar longitude in degrees, normalized to [-180, 180]. */
  lng: number;
  /** Equation of time, minutes. */
  eotMinutes: number;
  /** Unit vector in world space from Earth's centre toward the sun. */
  direction: Vector3;
}

// Analytic sun-position from J2000.0 — accurate to < 1' for centuries
// either side of epoch, far better than we need. No library required.
// References: U.S. Naval Observatory "Approximate Solar Coordinates"
// and Meeus, "Astronomical Algorithms".
export function computeSolarState(
  date: Date = new Date(),
  out = new Vector3(),
): SolarState {
  // Fractional days since J2000.0 (2000-01-01 12:00 UTC)
  const n = date.getTime() / 86_400_000 - 10957.5;

  // Mean longitude and mean anomaly of the Sun (degrees)
  const L = mod(280.46 + 0.9856474 * n, 360);
  const g = (mod(357.528 + 0.9856003 * n, 360) * Math.PI) / 180;

  // Ecliptic longitude (radians)
  const lambda =
    ((L + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * Math.PI) / 180;

  // Obliquity of the ecliptic (radians)
  const epsilon = ((23.439 - 0.0000004 * n) * Math.PI) / 180;

  // Right ascension and declination
  const alpha =
    (Math.atan2(Math.cos(epsilon) * Math.sin(lambda), Math.cos(lambda)) *
      180) /
    Math.PI;
  const delta =
    (Math.asin(Math.sin(epsilon) * Math.sin(lambda)) * 180) / Math.PI;

  // Equation of time in minutes (difference between mean and apparent sun)
  const eot = 4 * mod(L - alpha + 540, 360) - 720;

  // Subsolar longitude — the meridian where the sun is directly overhead
  const utcHours =
    date.getUTCHours() +
    date.getUTCMinutes() / 60 +
    date.getUTCSeconds() / 3600 +
    date.getUTCMilliseconds() / 3_600_000;
  const subsolarLng = wrapLng(-15 * (utcHours - 12) - eot / 4);

  latLngToVec3(delta, subsolarLng, 1, out);

  return {
    lat: delta,
    lng: subsolarLng,
    eotMinutes: eot,
    direction: out,
  };
}

function mod(x: number, m: number): number {
  return ((x % m) + m) % m;
}

function wrapLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}
